import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCustomerDebtReport,
  getCustomerOpenInvoices,
  getSettings,
  logCustomerInteraction,
  renderMessageTemplate,
  sendOpenBalanceWhatsApp,
  type CustomerOpenInvoice,
} from '../api/client';
import {
  summarizeOpenInvoicesByScope,
} from '../lib/openBalanceDebtScope';
import { normalizePhoneForWhatsAppLink } from '../lib/phone';
import type { CustomerDebtDetail } from './CustomerDetailsModal';
import EditMessageTemplateModal from './EditMessageTemplateModal';
import PaymentLinkWizardModal from './PaymentLinkWizardModal';
import { ReconciliationWarning } from './CustomerReconciliationPanel';

interface OpenBalanceWhatsAppModalProps {
  customerId: number;
  displayName?: string;
  phone?: string | null;
  fallbackOpenAmount?: number;
  fallbackAgedAmount?: number;
  fallbackAgeDays?: number;
  includeInvoiceBlockNotice?: boolean;
  messagesEnabled?: boolean;
  embedded?: boolean;
  onClose?: () => void;
}

function formatNIS(amount: number): string {
  return `₪${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatMonthLabel(month: string, locale?: string): string {
  const date = new Date(`${month}-01T00:00:00`);
  return date.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short' });
}

export default function OpenBalanceWhatsAppModal({
  customerId,
  displayName,
  phone,
  fallbackAgeDays = 60,
  includeInvoiceBlockNotice = false,
  messagesEnabled = false,
  embedded = false,
  onClose,
}: OpenBalanceWhatsAppModalProps) {
  const { t, i18n } = useTranslation();
  const [detail, setDetail] = useState<CustomerDebtDetail | null>(null);
  const [openInvoices, setOpenInvoices] = useState<CustomerOpenInvoice[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openInvoicesError, setOpenInvoicesError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [paymentLinksEnabled, setPaymentLinksEnabled] = useState(false);
  const [businessName, setBusinessName] = useState('קפה דאדא');
  const [tenantWhatsAppMessage, setTenantWhatsAppMessage] = useState('');
  const [tenantSmsMessage, setTenantSmsMessage] = useState('');
  const [templateRevision, setTemplateRevision] = useState(0);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [showPaymentLinkWizard, setShowPaymentLinkWizard] = useState(false);
  const [paymentLinkError, setPaymentLinkError] = useState<string | null>(null);
  const [whatsAppAudit, setWhatsAppAudit] = useState<{
    requestId: string;
    opened: boolean;
    phone: string;
    message: string;
    paymentLink?: string;
  } | null>(null);
  const [loggingWhatsApp, setLoggingWhatsApp] = useState(false);

  useEffect(() => {
    let active = true;
    getSettings()
      .then((response) => {
        const data = response.data?.settings ?? response.data ?? {};
        if (active) {
          setPaymentLinksEnabled(data.payment_links_enabled === true);
          setBusinessName(data.business_name || data.customer_bot_business_name || 'קפה דאדא');
        }
      })
      .catch(() => {
        if (active) setPaymentLinksEnabled(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    setLoading(true);
    setError(null);
    setOpenInvoicesError(null);
    setDetail(null);
    setOpenInvoices(null);

    Promise.allSettled([
      getCustomerDebtReport(customerId),
      getCustomerOpenInvoices(customerId),
    ]).then(([detailResult, invoicesResult]) => {
      if (!active) return;
      if (detailResult.status === 'fulfilled') {
        setDetail(detailResult.value.data);
      } else {
        setError(t('customers.detailsError'));
      }
      if (invoicesResult.status === 'fulfilled' && Array.isArray(invoicesResult.value.data)) {
        setOpenInvoices(invoicesResult.value.data);
      } else {
        // Leave openInvoices as null (not []) — scopedSummary/agedSummary stay null,
        // which correctly hides the debt composer instead of showing a misleading $0 balance.
        setOpenInvoicesError(t('reports.debtScopeLoadError'));
      }
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [customerId, fallbackAgeDays, t]);

  const resolvedName = detail?.customer_name || displayName || `#${customerId}`;
  const resolvedPhone = detail?.phone || phone;
  const previewAgeDays = detail?.payment_terms_days ?? fallbackAgeDays;
  const scopedSummary = useMemo(
    () => openInvoices
      ? summarizeOpenInvoicesByScope(openInvoices, previewAgeDays, 'all')
      : null,
    [openInvoices, previewAgeDays],
  );
  const agedSummary = useMemo(
    () => openInvoices
      ? summarizeOpenInvoicesByScope(openInvoices, previewAgeDays, 'aged')
      : null,
    [openInvoices, previewAgeDays],
  );
  const hasInvalidInvoiceDates = (scopedSummary?.invalidDateCount ?? 0) > 0;
  const hasReconciliationDifference = Boolean(detail?.reconciliation && (
    !detail.reconciliation.complete
    || Math.abs(detail.reconciliation.discrepancy ?? 0) > 0.01
  ));
  const hasBalanceDifference = hasReconciliationDifference
    || Boolean(detail && scopedSummary && Math.abs(detail.current_balance - scopedSummary.total) > 0.01);
  const whatsappTemplateValues = useMemo<Record<string, string> | null>(() => {
    if (!detail || !scopedSummary || scopedSummary.total <= 0 || hasInvalidInvoiceDates) return null;
    const thresholdDays = detail.payment_terms_days ?? 60;
    const agedOpen = agedSummary?.total ?? 0;
    const agedMonths = new Set((agedSummary?.months ?? []).map((month) => month.month));
    const monthlyLines = scopedSummary.months
      .map((month) => `${agedMonths.has(month.month) ? '⚠️ ' : ''}${formatMonthLabel(month.month, i18n.language)}: ${formatNIS(month.amount)}`)
      .join('\n');
    const blockNotice = includeInvoiceBlockNotice
      ? `\n\nבנוסף, מאחר שקיים חוב פתוח מעל ${thresholdDays} יום, לא ניתן ליצור הזמנה או חשבונית חדשה עד להסדרת החוב.`
      : '';
    const oldDebtLine = agedOpen > 0
      ? `מתוכו חוב ישן מעל ${thresholdDays} יום: *${formatNIS(agedOpen)}*\n\n`
      : '';
    const debtDetails = `${oldDebtLine}פירוט לפי חודש:\n${monthlyLines}${blockNotice}`;
    // No real link yet? Omit the line entirely — never send a placeholder to the customer.
    const paymentLinkLine = paymentLink ? `\nלתשלום מאובטח: ${paymentLink}` : '';
    return {
      customer_name: detail.customer_name || resolvedName,
      business_name: businessName,
      amount_due: formatNIS(scopedSummary.total),
      debt_details: debtDetails,
      payment_link_line: paymentLinkLine,
      payment_link: paymentLink || '',
    };
  }, [
    businessName,
    detail,
    agedSummary,
    hasInvalidInvoiceDates,
    i18n.language,
    includeInvoiceBlockNotice,
    paymentLink,
    resolvedName,
    scopedSummary,
  ]);

  const smsTemplateValues = useMemo<Record<string, string> | null>(() => {
    if (!detail || !scopedSummary || scopedSummary.total <= 0 || hasInvalidInvoiceDates) return null;
    const thresholdDays = detail.payment_terms_days ?? 60;
    const agedOpen = agedSummary?.total ?? 0;
    const agedClause = agedOpen > 0
      ? `, מתוכה ₪${agedOpen.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} חוב ישן מעל ${thresholdDays} יום`
      : '';
    const balanceSentence = `יש לך חוב כולל בסך ₪${scopedSummary.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${agedClause}`;
    return {
      customer_name: detail.customer_name || resolvedName,
      business_name: businessName,
      balance_sentence: balanceSentence,
      block_notice: includeInvoiceBlockNotice
        ? '\nלא ניתן ליצור הזמנה או חשבונית חדשה עד להסדרת החוב.'
        : '',
      payment_link_line: paymentLink ? `\nלתשלום מאובטח: ${paymentLink}` : '',
      total_open: scopedSummary.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      aged_open: agedOpen.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      debt_threshold_days: String(thresholdDays),
    };
  }, [
    agedSummary,
    businessName,
    detail,
    hasInvalidInvoiceDates,
    includeInvoiceBlockNotice,
    paymentLink,
    resolvedName,
    scopedSummary,
  ]);

  useEffect(() => {
    if (!whatsappTemplateValues) {
      setTenantWhatsAppMessage('');
      return;
    }
    let active = true;
    renderMessageTemplate('debt_whatsapp', whatsappTemplateValues)
      .then((response) => {
        if (active) setTenantWhatsAppMessage(response.data.body);
      })
      .catch(() => {
        if (active) setTenantWhatsAppMessage('');
      });
    return () => {
      active = false;
    };
  }, [templateRevision, whatsappTemplateValues]);

  useEffect(() => {
    if (!smsTemplateValues) {
      setTenantSmsMessage('');
      return;
    }
    let active = true;
    renderMessageTemplate('open_balance_sms', smsTemplateValues)
      .then((response) => {
        if (active) setTenantSmsMessage(response.data.body);
      })
      .catch(() => {
        if (active) setTenantSmsMessage('');
      });
    return () => {
      active = false;
    };
  }, [smsTemplateValues, templateRevision]);

  const message = tenantWhatsAppMessage;

  const handleCopy = useCallback(async () => {
    if (!message) return;
    try {
      await navigator.clipboard.writeText(message);
    } catch {
      // The visible preview remains available when clipboard access is denied.
    }
  }, [message]);

  const handleOpenPaymentLinkWizard = useCallback(() => {
    setPaymentLinkError(null);
    setShowPaymentLinkWizard(true);
  }, []);

  const handlePaymentLinkSuccess = useCallback((url: string) => {
    setPaymentLink(url);
    setShowPaymentLinkWizard(false);
  }, []);

  const handleSendSms = useCallback(async () => {
    if (!tenantSmsMessage || sending) return;
    setSending(true);
    setSendResult(null);
    setSendError(null);
    try {
      const response = await sendOpenBalanceWhatsApp(customerId, {
        include_invoice_block_notice: includeInvoiceBlockNotice,
        include_payment_link: paymentLinksEnabled,
        payment_link: paymentLink,
      });
      if (response.data.payment_link) setPaymentLink(response.data.payment_link);
      setSendResult(t('reports.smsSent', 'SMS debt notification sent to {{phone}}', { phone: response.data.phone }));
    } catch (err: any) {
      setSendError(err?.response?.data?.detail || t('reports.smsSendError', 'Failed to send SMS notification'));
    } finally {
      setSending(false);
    }
  }, [customerId, includeInvoiceBlockNotice, paymentLink, paymentLinksEnabled, sending, t, tenantSmsMessage]);

  const handleSendWhatsAppLink = useCallback(async () => {
    if (!resolvedPhone || !message) return;
    const audit = whatsAppAudit || {
      requestId: crypto.randomUUID(),
      opened: false,
      phone: resolvedPhone,
      message,
      paymentLink: paymentLink || undefined,
    };
    setSendError(null);
    setLoggingWhatsApp(true);
    if (!audit.opened) {
      const intlPhone = normalizePhoneForWhatsAppLink(audit.phone);
      window.open(`https://wa.me/${intlPhone}?text=${encodeURIComponent(audit.message)}`, '_blank', 'noopener,noreferrer');
      audit.opened = true;
      setWhatsAppAudit(audit);
    }
    try {
      await logCustomerInteraction(customerId, {
        channel: 'whatsapp',
        message_type: 'open_balance_sent',
        selected_action: 'open_balance_handoff_opened',
        destination: audit.phone,
        message_text: audit.message,
        related_payment_link_id: audit.paymentLink,
        request_id: audit.requestId,
      });
      setWhatsAppAudit(null);
      setSendResult(t('customerBot.whatsAppHandoffLogged', 'WhatsApp opened and the communication was added to customer history.'));
    } catch (err: any) {
      setSendError(err?.response?.data?.detail || t('customerBot.interactionSaveError', 'WhatsApp opened, but communication history could not be saved. Press again to retry saving without opening another draft.'));
    } finally {
      setLoggingWhatsApp(false);
    }
  }, [customerId, message, paymentLink, resolvedPhone, t, whatsAppAudit]);

  return (
    <div
      className={embedded ? '' : 'fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 p-4'}
      onClick={embedded ? undefined : onClose}
    >
      <div
        className={embedded ? 'w-full rounded-xl border border-gray-200 bg-white' : 'max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl'}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div>
            <p className="text-sm font-medium text-blue-700">{t('documents.sendSms', 'Send SMS')}</p>
            <h2 className="text-lg font-bold text-gray-900">{resolvedName}</h2>
            <p className="mt-1 text-sm text-gray-500">{resolvedPhone || t('documents.noPhoneNumber')}</p>
          </div>
          {!embedded && (
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50">
              {t('common.close', 'Close')}
            </button>
          )}
        </div>

        <div className="space-y-4 px-5 py-5">
          {!messagesEnabled && (
            <div role="alert" className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <span aria-hidden="true">⚠️</span>
              <span>{t('reports.messagingBlockedWarning')}</span>
            </div>
          )}

          {loading && (
            <div className="py-8 text-center text-sm text-gray-500">{t('reports.loadingCustomerDetails')}</div>
          )}
          {error && !loading && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}
          {openInvoicesError && !loading && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {openInvoicesError}
            </div>
          )}
          {hasInvalidInvoiceDates && !loading && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {t('reports.debtScopeInvalidDates')}
            </div>
          )}
          {sendResult && (
            <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{sendResult}</div>
          )}
          {sendError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{sendError}</div>
          )}
          {paymentLinkError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{paymentLinkError}</div>
          )}

          {!loading && detail && scopedSummary && (
            <>
              <ReconciliationWarning data={detail.reconciliation} />
              {hasBalanceDifference && (
                <p className="text-sm text-amber-900">
                  {t('reports.debtPreviewScopeNotice')}
                  {!hasReconciliationDifference && <span className="block">{t('collectionWorkflow.account')}: {formatNIS(detail.current_balance)}</span>}
                </p>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="text-xs font-medium text-amber-700">
                    {hasBalanceDifference
                      ? t('reports.knownInvoiceDebt')
                      : t('reports.selectedAllDebt')}
                  </div>
                  <div className="mt-1 text-xl font-bold text-amber-900">
                    {formatNIS(scopedSummary.total)}
                  </div>
                </div>
                {(agedSummary?.total ?? 0) > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <div className="text-xs font-medium text-red-700">
                      {t('reports.selectedOldDebt', { days: previewAgeDays })}
                    </div>
                    <div className="mt-1 text-xl font-bold text-red-900">
                      {formatNIS(agedSummary?.total ?? 0)}
                    </div>
                  </div>
                )}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs font-medium text-gray-600">{t('reports.debtAgeThreshold')}</div>
                  <div className="mt-1 text-xl font-bold text-gray-900">
                    {t('reports.paymentTermsDays', { days: previewAgeDays })}
                  </div>
                </div>
              </div>

              {paymentLinksEnabled && paymentLink && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                  <div className="text-xs font-medium text-green-700">{t('reports.paymentLink')}</div>
                  <a href={paymentLink} target="_blank" rel="noreferrer" className="mt-1 block break-all text-sm font-medium text-green-800 underline">
                    {paymentLink}
                  </a>
                </div>
              )}

              {whatsappTemplateValues ? (
                <div className="space-y-4">
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="text-sm font-medium text-gray-700">{t('reports.whatsAppMessagePreview', 'WhatsApp message')}</div>
                      {whatsappTemplateValues && (
                        <EditMessageTemplateModal
                          templateKey="debt_whatsapp"
                          values={whatsappTemplateValues}
                          onChanged={() => setTemplateRevision((current) => current + 1)}
                        />
                      )}
                    </div>
                    <div className="max-h-[30vh] overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-800">
                      {message || t('settings.messageTemplates.previewUnavailable')}
                    </div>
                  </div>
                  {smsTemplateValues && (
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <div className="text-sm font-medium text-gray-700">{t('reports.smsMessagePreview', 'SMS message')}</div>
                        <EditMessageTemplateModal
                          templateKey="open_balance_sms"
                          values={smsTemplateValues}
                          onChanged={() => setTemplateRevision((current) => current + 1)}
                        />
                      </div>
                      <div className="max-h-[30vh] overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-800">
                        {tenantSmsMessage || t('settings.messageTemplates.previewUnavailable')}
                      </div>
                    </div>
                  )}
                </div>
              ) : !openInvoicesError && !hasInvalidInvoiceDates ? (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  {t('reports.noDebtForScope')}
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <button
            type="button"
            onClick={handleCopy}
            disabled={!message}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('reports.copyMessage')}
          </button>
          {paymentLinksEnabled && (
            <button
              type="button"
              onClick={handleOpenPaymentLinkWizard}
              disabled={!!paymentLink}
              className="rounded-lg border border-green-300 bg-white px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {paymentLink ? t('reports.paymentLinkAdded') : t('reports.addPaymentLink')}
            </button>
          )}
          <span className={!messagesEnabled ? 'cursor-not-allowed' : ''} title={!messagesEnabled ? t('reports.messagingNotEnabled') : undefined}>
            <button
              type="button"
              onClick={handleSendWhatsAppLink}
              disabled={!message || !resolvedPhone || !messagesEnabled || loggingWhatsApp}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {loggingWhatsApp ? t('common.saving', 'Saving...') : t('reports.sendDebtWhatsAppLink')}
            </button>
          </span>
          <span className={!messagesEnabled ? 'cursor-not-allowed' : ''} title={!messagesEnabled ? t('reports.messagingNotEnabled') : undefined}>
            <button
              type="button"
              onClick={handleSendSms}
              disabled={!tenantSmsMessage || sending || !messagesEnabled}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {sending ? t('settings.permittedNumbers.sending') : t('documents.sendSms')}
            </button>
          </span>
        </div>
      </div>

      {showPaymentLinkWizard && (
        <PaymentLinkWizardModal
          customerId={customerId}
          customerName={resolvedName}
          onClose={() => setShowPaymentLinkWizard(false)}
          onSuccess={handlePaymentLinkSuccess}
        />
      )}
    </div>
  );
}
